import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {VelocityTransitionGroup} from 'velocity-react';
import {connect} from 'react-redux';
import {actions, select} from './connectors';
import {matchPath} from 'react-router-dom';
// constants
import {
  SHOPPING_CART_CONFIRM,
  SHOPPING_CART_CONFIRM_DIRTY
} from '~/constants/NotificationConstants';
import {SAVE, WELCOME, WELCOME_BACK} from '~/constants/WelcomeConstants';
import Animations from '~/constants/Animations';
import MediaQueries, {breakpoint} from '~/constants/MediaQueries';
// components
import MainMenuBar from '~/components/MainMenuBar';
import Welcome_Lg from './Welcome_Lg';
import SaveCTA from './SaveCTA';
import {isEditDesignInCartMode} from '~/utils/url';

import withRouter, {routerPropTypes} from '~/router/withRouter';
import {metrics} from '~/vendor/metrics';
import {getCheckoutSummaryLink} from '../../constants/CheckoutLink';

const propTypes = {
  router: routerPropTypes.isRequired,
  analytics: PropTypes.shape({
    productColor: PropTypes.func,
    productWelcomeTag: PropTypes.func,
    textStart: PropTypes.func,
    uploadXPStart: PropTypes.func,
    welcome: PropTypes.func,
    welcomeBack: PropTypes.func,
    welcomeBackContinue: PropTypes.func,
    welcomeBackNew: PropTypes.func
  }),
  cartItemCount: PropTypes.number,
  clear: PropTypes.func,
  designName: PropTypes.string,
  fetchSavedDesigns: PropTypes.func,
  isCurrentDesignInCart: PropTypes.bool,
  isDesignDirty: PropTypes.bool,
  isFundraisingMode: PropTypes.bool,
  isMultiItemEligible: PropTypes.bool,
  notification: PropTypes.object,
  prompt: PropTypes.func,
  savedDesignsLength: PropTypes.number,
  welcome: PropTypes.string
};

/** @extends {Component<PropTypes.InferProps<typeof propTypes>>} */
export class WelcomeContainer extends Component {
  static displayName = 'WelcomeContainer';
  static propTypes = propTypes;

  state = {
    break: breakpoint(MediaQueries.largeAndAbove),
    welcome: this.props.welcome
  };

  componentDidMount() {
    this.mediaQuery = window.matchMedia(MediaQueries.largeAndAbove);
    this.mediaQuery.addListener(this.mql);
  }

  componentWillUnmount() {
    this.mediaQuery.removeListener(this.mql);
  }

  isStartFromDesignPage() {
    return matchPath(
      {path: '/startFromDesign'},
      this.props.router.location.pathname
    );
  }

  mql = mq => this.setState({break: mq.matches});

  newDesignAction = e => {
    if (e) e.stopPropagation();
    this.props.clear();
    this.props.analytics.welcomeBackNew();
    this.props.router.navigate('/');
    if (this.state.break) {
      this.setState({welcome: WELCOME});
    }
  };

  renderWelcomeBack = () => {
    if (this.state.break) {
      return (
        <Welcome_Lg
          cartItemCount={this.props.cartItemCount}
          designName={this.props.designName}
          isCurrentDesignInCart={this.props.isCurrentDesignInCart}
          isEditFromCart={isEditDesignInCartMode()}
          isFundraisingMode={this.props.isFundraisingMode}
          isMultiItemEligible={this.props.isMultiItemEligible}
          isStartFromDesignPage={this.isStartFromDesignPage()}
          welcomeBack
          onAddArt={this.addArtAction}
          onAddText={this.addTextAction}
          onChangeProductColor={this.changeProductColorAction}
          onCheckout={this.checkoutAction}
          onNew={this.newDesignAction}
          onProduct={this.productAction}
          onRetrieveDesigns={this.retrieveDesignsAction}
          onTextStart={this.props.analytics.textStart}
          onStart={this.startAction}
          onUpload={this.uploadAction}
        />
      );
    } else {
      return <MainMenuBar router={this.props.router} />;
    }
  };

  startAction = e => {
    e.stopPropagation();
    this.props.router.navigate('/');
  };

  navigateWithCustomDimension = (e, path, customDimension = '') => {
    e.stopPropagation();
    this.props.router.navigate(`${path}${customDimension}`);
  };

  addArtAction = (e, customDimension) => {
    this.navigateWithCustomDimension(e, '/addArt', customDimension);
  };

  addTextAction = (e, customDimension) => {
    this.navigateWithCustomDimension(e, '/addText', customDimension);
  };

  changeProductColorAction = (e, customDimension) => {
    this.props.analytics.productColor('welcome');
    this.navigateWithCustomDimension(e, '/productColor', customDimension);
  };

  checkoutAction = (e, customDimension) => {
    this.fireCustomDimension(customDimension);
    metrics.pageViewTag('ndx/reviewcart');
    if (this.props.isDesignDirty) {
      const key = isEditDesignInCartMode()
        ? SHOPPING_CART_CONFIRM
        : SHOPPING_CART_CONFIRM_DIRTY;
      this.props.prompt({key: key});
    } else {
      window.location.href = getCheckoutSummaryLink();
    }
  };

  productAction = (e, customDimension) => {
    this.props.analytics.productWelcomeTag();
    this.navigateWithCustomDimension(e, '/productCatalog', customDimension);
  };

  fireCustomDimension = customDimension => {
    if (!customDimension) return;
    let location;
    switch (customDimension.split('=')[1]) {
      case 'wb':
        location = 'welcomeback';
        break;
      case 'rs':
        location = 'restingscreen';
        break;
      case 'w':
        location = 'welcome';
        break;
      default:
        location = 'toolbar';
    }
    metrics.setDimension('RestingScreenClick', location);
  };

  retrieveDesignsAction = (e, customDimension) => {
    this.fireCustomDimension(customDimension);
    metrics.pageViewTag('ndx/retrieve/design');
    if (this.props.savedDesignsLength === 0) {
      this.props.fetchSavedDesigns(1).then(() => {
        this.navigateWithCustomDimension(e, '/savedDesigns', customDimension);
      });
    } else {
      this.navigateWithCustomDimension(e, '/savedDesigns', customDimension);
    }
  };

  uploadAction = (e, customDimension) => {
    this.navigateWithCustomDimension(e, '/uploadForm', customDimension);
    this.props.analytics.uploadXPStart({
      source: 'wb'
    });
  };

  saveAction = (e, customDimension) => {
    this.navigateWithCustomDimension(e, '/saveForm', customDimension);
  };

  renderWelcome = () => {
    if (this.state.break) {
      return (
        <Welcome_Lg
          cartItemCount={this.props.cartItemCount}
          designName={this.props.designName}
          isCurrentDesignInCart={this.props.isCurrentDesignInCart}
          isEditFromCart={isEditDesignInCartMode()}
          isFundraisingMode={this.props.isFundraisingMode}
          isMultiItemEligible={this.props.isMultiItemEligible}
          isStartFromDesignPage={this.isStartFromDesignPage()}
          onAddArt={this.addArtAction}
          onAddText={this.addTextAction}
          onChangeProductColor={this.changeProductColorAction}
          onCheckout={this.checkoutAction}
          onNew={this.newDesignAction}
          onProduct={this.productAction}
          onRetrieveDesigns={this.retrieveDesignsAction}
          onStart={this.startAction}
          onTextStart={this.props.analytics.textStart}
          onUpload={this.uploadAction}
        />
      );
    } else {
      return <MainMenuBar router={this.props.router} welcoming />;
    }
  };

  renderSaveCTA = customDimension => (
    <SaveCTA onFloatingLinkClick={e => this.saveAction(e, customDimension)} />
  );

  renderContent() {
    const customDimension = '?rs=rs';
    switch (this.state.welcome) {
      case WELCOME:
        return this.renderWelcome();
      case WELCOME_BACK:
        return this.renderWelcomeBack();
      case SAVE:
        return this.renderSaveCTA(customDimension);
      default:
        return this.renderWelcome();
    }
  }

  render() {
    const content = this.renderContent();
    return (
      !!content && (
        <div>
          <VelocityTransitionGroup leave={Animations.fadeOut}>
            {content}
          </VelocityTransitionGroup>
        </div>
      )
    );
  }
}

export default connect(
  state => select(state),
  dispatch => actions(dispatch)
)(withRouter(WelcomeContainer));
